// src/pages/FinanceCashbookPage.jsx
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/FinanceCashbook.css";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const REVIEW_ROLES = [
  "admin",
  "super-admin",
  "manager",
  "Manager",
  "finance-head",
];

export default function FinanceCashbookPage() {
  const { user } = useContext(AuthContext);

  const role = String(user?.role || "").toLowerCase();
  const isReviewer = REVIEW_ROLES.map((r) => String(r).toLowerCase()).includes(
    role
  );

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  /* ---------------- helpers ---------------- */
  const toId = useCallback((v) => String(v || "").trim(), []);
  const safeLower = (v) => String(v || "").toLowerCase();

  const fmt = (n) =>
    Number.isFinite(Number(n))
      ? Number(n).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : "-";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isWithinHours = (date, hours = 24) => {
    if (!date) return false;
    return Date.now() - new Date(date).getTime() <= hours * 3600 * 1000;
  };

  const isOwnerRow = (row) => {
    if (!row || row._id === "__opening__") return false;
    return row.user && user?._id && String(row.user) === String(user._id);
  };

  const canSelfModify = (row) =>
    isOwnerRow(row) && isWithinHours(row.createdAt, 24);

  const canDeleteWithinDays = (row, days = 15) => {
    if (!isOwnerRow(row)) return false;

    const baseDate = row?.createdAt || row?.date;
    if (!baseDate) return false;

    const diff = Date.now() - new Date(baseDate).getTime();
    return diff <= days * 24 * 60 * 60 * 1000;
  };

  /* ---------------- toast ---------------- */
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);

  const showToast = useCallback((type, msg, duration = 2200) => {
    setToast({ type, msg });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  useEffect(() => {
    return () => toastRef.current && clearTimeout(toastRef.current);
  }, []);

  /* ---------------- bootstrap lists ---------------- */
  const [people, setPeople] = useState([]);
  const [masterSites, setMasterSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [works, setWorks] = useState([]);

  const [errors, setErrors] = useState({});
  const setErr = (k, v) =>
    setErrors((p) => ({ ...p, [k]: v ? String(v) : "" }));

  const fetchUsers = useCallback(async () => {
    if (!isReviewer) return;
    try {
      const r = await axios.get("/api/users", authHeader);
      setPeople(r.data || []);
      setErr("users", "");
    } catch (e) {
      setPeople([]);
      setErr(
        "users",
        `/api/users → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
        }`
      );
    }
  }, [authHeader, isReviewer]);

  const fetchMasterSites = useCallback(async () => {
    const tries = ["/api/mastersites", "/api/masterSites"];
    for (const path of tries) {
      try {
        const r = await axios.get(path, authHeader);
        if (Array.isArray(r.data)) {
          setMasterSites(r.data);
          setErr("masterSites", "");
          return;
        }
      } catch (e) {
        setErr(
          "masterSites",
          `${path} → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
          }`
        );
      }
    }
  }, [authHeader]);

  const fetchSites = useCallback(async () => {
    try {
      const r = await axios.get("/api/sites", authHeader);
      setSites(Array.isArray(r.data) ? r.data : []);
      setErr("sites", "");
    } catch (e) {
      setSites([]);
      setErr(
        "sites",
        `/api/sites → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
        }`
      );
    }
  }, [authHeader]);

  const fetchWorks = useCallback(async () => {
    try {
      const r = await axios.get("/api/works", authHeader);
      setWorks(Array.isArray(r.data) ? r.data : []);
      setErr("works", "");
    } catch (e) {
      setWorks([]);
      setErr(
        "works",
        `/api/works → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
        }`
      );
    }
  }, [authHeader]);

  useEffect(() => {
    fetchUsers();
    fetchMasterSites();
    fetchSites();
    fetchWorks();
  }, [fetchUsers, fetchMasterSites, fetchSites, fetchWorks]);

  /* ---------------- filters (REVIEW) ---------------- */
  const [qSearch, setQSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [onlyMyEntries, setOnlyMyEntries] = useState(false);

  const [locationType, setLocationType] = useState("");
  const [workFilterId, setWorkFilterId] = useState("");
  const [masterFilterId, setMasterFilterId] = useState("");
  const [siteFilterId, setSiteFilterId] = useState("");

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const clearLocation = () => {
    setLocationType("");
    setWorkFilterId("");
    setMasterFilterId("");
    setSiteFilterId("");
  };

  const getSiteMasterIdForFilter = (s) =>
    String(s?.masterSiteId || s?.masterSite || s?.masterSite?._id || "");

  const reviewSitesUnderMaster = useMemo(() => {
    if (!masterFilterId) return [];
    return (sites || []).filter(
      (s) => getSiteMasterIdForFilter(s) === String(masterFilterId)
    );
  }, [sites, masterFilterId]);

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();

    if (isReviewer) {
      if (onlyMyEntries && user?._id) {
        q.append("userId", user._id);
      } else if (selectedUserId) {
        q.append("userId", selectedUserId);
      }
    } else if (user?._id) {
      q.append("userId", user._id);
    }

    if (locationType === "work" && workFilterId) {
      q.append("work", workFilterId);
    }

    if (locationType === "masterSite") {
      if (siteFilterId) q.append("site", siteFilterId);
      else if (masterFilterId) q.append("masterSite", masterFilterId);
    }

    if (from) q.append("from", from);
    if (to) q.append("to", to);

    return q.toString();
  }, [
    isReviewer,
    onlyMyEntries,
    selectedUserId,
    user?._id,
    locationType,
    workFilterId,
    masterFilterId,
    siteFilterId,
    from,
    to,
  ]);

  /* ---------------- loaders ---------------- */
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);

  const loadListAndSummary = useCallback(async () => {
    const qs = buildQuery();
    setErr("list", "");
    setErr("summary", "");

    try {
      const list = await axios.get(`/api/cashbook?${qs}`, authHeader);
      setRows(list.data || []);
    } catch (e) {
      setRows([]);
      setErr(
        "list",
        `/api/cashbook → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
        }`
      );
    }

    try {
      const sum = await axios.get(`/api/cashbook/summary?${qs}`, authHeader);
      setSummary(sum.data || null);
    } catch (e) {
      setSummary(null);
      setErr(
        "summary",
        `/api/cashbook/summary → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
        }`
      );
    }
  }, [authHeader, buildQuery]);

  const loadBalance = useCallback(async () => {
    setErr("balance", "");

    const effectiveSelectedUserId =
      isReviewer && onlyMyEntries ? user?._id : selectedUserId;

    if (isReviewer && !effectiveSelectedUserId) {
      setBalance(null);
      setErr("balance", "Select an employee to see balance");
      return;
    }

    const uid = isReviewer ? effectiveSelectedUserId : user?._id;
    if (!uid) {
      setBalance(null);
      return;
    }

    try {
      const q = new URLSearchParams();
      q.append("userId", uid);
      const r = await axios.get(
        `/api/cashbook/balance?${q.toString()}`,
        authHeader
      );
      setBalance(r.data?.balance ?? null);
    } catch (e) {
      setBalance(null);
      setErr(
        "balance",
        `/api/cashbook/balance → ${e?.response?.status || ""} ${e?.response?.data?.message || e.message
        }`
      );
    }
  }, [authHeader, isReviewer, onlyMyEntries, selectedUserId, user?._id]);

  useEffect(() => {
    loadListAndSummary();
    loadBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isReviewer) return;
    loadListAndSummary();
    loadBalance();
  }, [onlyMyEntries, isReviewer, loadListAndSummary, loadBalance]);

  const applyNow = async () => {
    await Promise.all([loadListAndSummary(), loadBalance()]);
    showToast("info", "Applied");
  };

  /* ---------------- derived rows ---------------- */
  const derivedRows = useMemo(() => {
    const chronological = (rows || [])
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.date || a.createdAt || 0).getTime();
        const tb = new Date(b.date || b.createdAt || 0).getTime();
        return ta - tb;
      });

    let run = Number(summary?.openingBalance || 0);
    return chronological.map((r) => {
      const amt = Number(r.amountINR || 0);
      const delta = r.type === "cash_out" ? -amt : amt;
      run += delta;
      return { ...r, __runningBalance: run };
    });
  }, [rows, summary]);

  const openingRow = useMemo(() => {
    if (!summary || summary.openingBalance == null) return null;

    let who = user?.username || "-";
    if (isReviewer) {
      if (onlyMyEntries) {
        who = user?.username || "My Entries";
      } else {
        who = selectedUserId
          ? people.find((p) => String(p._id) === String(selectedUserId))
            ?.username || "Selected employee"
          : "All employees";
      }
    }

    return {
      _id: "__opening__",
      date: from || null,
      userName: who,
      type: "opening",
      amountINR: Number(summary.openingBalance || 0),
      mode: "",
      masterSiteName: "",
      siteName: "",
      workName: "",
      remark: "Opening balance",
      attachments: [],
      __runningBalance: Number(summary.openingBalance || 0),
    };
  }, [
    summary,
    isReviewer,
    onlyMyEntries,
    selectedUserId,
    people,
    user?.username,
    from,
  ]);

  const displayRawRows = useMemo(() => {
    const newestFirst = derivedRows.slice().reverse();
    if (openingRow) return [...newestFirst, openingRow];
    return newestFirst;
  }, [derivedRows, openingRow]);

  const visibleRows = useMemo(() => {
    const q = safeLower(qSearch).trim();
    if (!q) return displayRawRows;

    return displayRawRows.filter((r) => {
      const text = [
        r.userName || r.user || "",
        r.siteName || "",
        r.masterSiteName || "",
        r.workName || "",
        r.remark || "",
        r.mode || "",
        r.type || "",
        String(r.amountINR ?? ""),
        fmtDate(r.date),
      ]
        .join(" | ")
        .toLowerCase();

      return text.includes(q);
    });
  }, [displayRawRows, qSearch]);

  /* ---------------- pagination (table) ---------------- */
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [
    qSearch,
    selectedUserId,
    onlyMyEntries,
    locationType,
    workFilterId,
    masterFilterId,
    siteFilterId,
    from,
    to,
  ]);

  const totalPages = useMemo(() => {
    const n = Math.ceil((visibleRows.length || 0) / pageSize);
    return n || 1;
  }, [visibleRows.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visibleRows.slice(start, start + pageSize);
  }, [visibleRows, page, pageSize]);

  /* ---------------- add modal (SELF ONLY) ---------------- */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    type: "cash_out",
    amountINR: "",
    mode: "Cash",
    remark: "",
    masterSite: "",
    site: "",
    work: "",
    attachments: [],
  });

  const [siteMasterFilterId, setSiteMasterFilterId] = useState("");
  const getSiteMasterId = (s) =>
    String(s?.masterSiteId || s?.masterSite || s?.masterSite?._id || "");

  const filteredSitesForAddDropdown = useMemo(() => {
    if (!siteMasterFilterId) return sites || [];
    return (sites || []).filter(
      (s) => getSiteMasterId(s) === String(siteMasterFilterId)
    );
  }, [sites, siteMasterFilterId]);

  const fileInputRef = useRef(null);

  const isBrowser = typeof window !== "undefined";
  const isLocalhost =
    isBrowser && window.location && window.location.hostname === "localhost";
  const apiBase =
    process.env.REACT_APP_SERVER_URL ||
    (isLocalhost ? "http://localhost:5000" : "");

  const makeFullUrl = (rawUrl) => {
    if (!rawUrl) return "#";
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
    if (!isLocalhost) return `/${rawUrl.replace(/^\/+/, "")}`;
    const base = (apiBase || "").replace(/\/$/, "");
    const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    return base ? `${base}${path}` : rawUrl;
  };

  const getAttachmentUrl = (att) => {
    if (!att) return "#";
    const rawUrl = typeof att === "string" ? att : att.url || "";
    return makeFullUrl(rawUrl);
  };

  const getAttachmentLabel = (att, idx) => {
    if (!att) return `Receipt ${idx + 1}`;
    if (typeof att === "string") return `Receipt ${idx + 1}`;
    return att.originalName || `Receipt ${idx + 1}`;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post("/api/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = res.data || {};
      const rawUrl = data.url || data.path || data.location || data.fileUrl || "";
      const attachmentObj =
        typeof data === "string"
          ? { url: data, originalName: file.name, mimeType: file.type }
          : {
            url: rawUrl,
            originalName: data.originalName || data.name || file.name,
            mimeType: data.mimeType || file.type || "",
          };

      setForm((f) => ({
        ...f,
        attachments: [...(f.attachments || []), attachmentObj],
      }));
      showToast("success", "Uploaded ✅");
    } catch (err) {
      showToast("error", "Upload failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const resetAddForm = () => {
    setForm({
      date: new Date().toISOString().slice(0, 10),
      type: "cash_out",
      amountINR: "",
      mode: "Cash",
      remark: "",
      masterSite: "",
      site: "",
      work: "",
      attachments: [],
    });
    setSiteMasterFilterId("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (saving) return;

    const msId = toId(form.masterSite);
    const sId = toId(form.site);
    const wId = toId(form.work);

    const chosen = [msId, sId, wId].filter(Boolean);
    if (chosen.length !== 1) {
      showToast(
        "error",
        chosen.length === 0
          ? "Select MasterSite OR Site OR Work"
          : "Choose ONLY one: MasterSite OR Site OR Work"
      );
      return;
    }

    const attachmentsArray = (form.attachments || []).map((att) => {
      if (typeof att === "string") {
        return { url: att, originalName: "", mimeType: "" };
      }
      return {
        url: att.url,
        originalName: att.originalName || "",
        mimeType: att.mimeType || "",
      };
    });

    const payload = {
      userId: user?._id,
      date: form.date,
      type: form.type,
      amountINR: Number(form.amountINR || 0),
      remark: form.remark,
      mode: form.mode,
      ...(msId ? { masterSite: msId } : {}),
      ...(sId ? { site: sId } : {}),
      ...(wId ? { work: wId } : {}),
      attachments: attachmentsArray,
    };

    setSaving(true);
    try {
      await axios.post("/api/cashbook", payload, authHeader);
      showToast("success", "Saved ✅");
      resetAddForm();
      setDrawerOpen(false);
      await applyNow();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Save failed";
      showToast("error", msg);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- edit ---------------- */
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "",
    type: "cash_out",
    amountINR: "",
    mode: "Cash",
    remark: "",
  });

  const openEdit = (row) => {
    if (!canSelfModify(row)) {
      return showToast(
        "error",
        "You can edit only your own entry within 24 hours."
      );
    }

    setEditing(row);
    setEditForm({
      date: row.date
        ? new Date(row.date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      type: row.type || "cash_out",
      amountINR: row.amountINR ?? "",
      mode: row.mode || "Cash",
      remark: row.remark || "",
    });
  };

  const closeEdit = () => setEditing(null);

  const submitEdit = async () => {
    if (!editing?._id) return;
    try {
      await axios.patch(
        `/api/cashbook/${editing._id}`,
        {
          date: editForm.date,
          type: editForm.type,
          amountINR: Number(editForm.amountINR || 0),
          mode: editForm.mode,
          remark: editForm.remark,
        },
        authHeader
      );
      showToast("success", "Updated ✅");
      closeEdit();
      await applyNow();
    } catch (err) {
      showToast("error", "Update failed");
    }
  };

  const handleDelete = async (row) => {
    if (!row?._id || row._id === "__opening__") return;

    if (!canDeleteWithinDays(row, 15)) {
      return showToast(
        "error",
        "You can delete only your own entries within 15 days."
      );
    }

    if (!window.confirm("Delete this entry?")) return;

    try {
      await axios.delete(`/api/cashbook/${row._id}`, authHeader);
      showToast("success", "Deleted ✅");
      await applyNow();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Delete failed";
      showToast("error", msg);
    }
  };

  /* ---------------- export ---------------- */
  const safeFilePart = (s) =>
    String(s || "")
      .trim()
      .replace(/[^\w\-]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);

  const currentEmployeeLabel = useMemo(() => {
    if (!isReviewer) return user?.username || "Me";

    if (onlyMyEntries) return user?.username || "My Entries";

    if (selectedUserId) {
      return (
        people.find((p) => String(p._id) === String(selectedUserId))?.username ||
        "Selected Employee"
      );
    }

    const nonOpeningRows = (visibleRows || []).filter((r) => r._id !== "__opening__");

    if (nonOpeningRows.length > 0) {
      const uniqueNames = [
        ...new Set(
          nonOpeningRows
            .map((r) => String(r.userName || r.user || "").trim())
            .filter(Boolean)
        ),
      ];

      if (uniqueNames.length === 1) {
        return uniqueNames[0];
      }
    }

    return "All Employees";
  }, [isReviewer, onlyMyEntries, selectedUserId, people, visibleRows, user?.username]);

  const getLocationLabel = () => {
    if (!locationType) return "All Locations";

    if (locationType === "work") {
      const w = works.find(
        (x) => String(x?._id || x?.id) === String(workFilterId)
      );
      const wName = w?.name || w?.title || w?.label || "";
      return `Work: ${wName || "Selected Work"}`;
    }

    if (locationType === "masterSite") {
      const ms = masterSites.find(
        (x) => String(x?._id) === String(masterFilterId)
      );
      const s = sites.find((x) => String(x?._id) === String(siteFilterId));
      if (siteFilterId) {
        return `Site: ${s?.name || "Selected Site"} (Master: ${ms?.name || "—"
          })`;
      }
      if (masterFilterId)
        return `MasterSite: ${ms?.name || "Selected MasterSite"}`;
      return "MasterSite: All";
    }

    return "All Locations";
  };

  const getDateRangeLabel = () => {
    const f = from ? fmtDate(from) : "Start";
    const t = to ? fmtDate(to) : "Today";
    return `${f} → ${t}`;
  };

  const computeReceivable = () => {
    const opening = Number(summary?.openingBalance || 0);
    const totalIn = Number(summary?.totalCashIn || 0);
    const totalOut = Number(summary?.totalCashOut || 0);
    return Math.max(0, totalOut - (opening + totalIn));
  };

  const computeAdvanceLeft = () => {
    const opening = Number(summary?.openingBalance || 0);
    const totalIn = Number(summary?.totalCashIn || 0);
    const totalOut = Number(summary?.totalCashOut || 0);
    return Math.max(0, opening + totalIn - totalOut);
  };

  const buildExportRows = () =>
    visibleRows.map((r) => ({
      Date: r.date ? new Date(r.date).toLocaleDateString("en-IN") : "—",
      Employee: r.userName || r.user || "-",
      Type: String(r.type || "").replaceAll("_", " "),
      "Received (Cash In) ₹":
        r.type === "cash_in" || r.type === "opening" ? fmt(r.amountINR) : "",
      "Spent (Cash Out) ₹": r.type === "cash_out" ? fmt(r.amountINR) : "",
      "Running Balance ₹": fmt(r.__runningBalance),
      Mode: r.mode || "",
      Location: r.siteName || r.masterSiteName || r.workName || "",
      Receipt: (r.attachments || [])
        .map((att, idx) =>
          typeof att === "string"
            ? att
            : att.url || att.originalName || `Receipt ${idx + 1}`
        )
        .filter(Boolean)
        .join(" ; "),
      Remark: r.remark || "-",
    }));

  const buildReportMeta = () => {
    const now = new Date();
    const generatedAt = now.toLocaleString("en-IN", { hour12: true });

    const opening = Number(summary?.openingBalance || 0);
    const totalIn = Number(summary?.totalCashIn || 0);
    const totalOut = Number(summary?.totalCashOut || 0);
    const finalBal = Number(summary?.finalBalance || 0);
    const balNow = balance == null ? null : Number(balance || 0);

    const receivable = computeReceivable();
    const advanceLeft = computeAdvanceLeft();

    return {
      generatedAt,
      employeeLabel: currentEmployeeLabel,
      locationLabel: getLocationLabel(),
      rangeLabel: getDateRangeLabel(),
      opening,
      totalIn,
      totalOut,
      receivable,
      advanceLeft,
      finalBal,
      balNow,
      rowCount: visibleRows.length,
    };
  };

  const handleExport = (kind) => {
    const exportRows = buildExportRows();
    if (!exportRows.length) return showToast("info", "No data to export.");

    const meta = buildReportMeta();

    const fileBase = [
      "Cashbook",
      safeFilePart(meta.employeeLabel),
      safeFilePart(from || "start"),
      "to",
      safeFilePart(to || "today"),
    ]
      .filter(Boolean)
      .join("_");

    if (kind === "xlsx") {
      const summaryRows = [
        { Field: "Report", Value: "Cashbook Report" },
        { Field: "Generated At", Value: meta.generatedAt },
        { Field: "Employee", Value: meta.employeeLabel },
        { Field: "Location", Value: meta.locationLabel },
        { Field: "Date Range", Value: meta.rangeLabel },
        { Field: "Total Rows", Value: meta.rowCount },
        { Field: "", Value: "" },
        { Field: "Opening (₹)", Value: meta.opening },
        { Field: "Total Cash In / Received (₹)", Value: meta.totalIn },
        { Field: "Total Cash Out / Spent (₹)", Value: meta.totalOut },
        {
          Field: "Receivable (Company → Employee) (₹)",
          Value: meta.receivable,
        },
        { Field: "Advance Left (Employee holds) (₹)", Value: meta.advanceLeft },
        { Field: "Final Balance (₹)", Value: meta.finalBal },
        {
          Field: "Balance Now (₹)",
          Value: meta.balNow == null ? "" : meta.balNow,
        },
      ];

      const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
      const wsEntries = XLSX.utils.json_to_sheet(exportRows);

      wsSummary["!cols"] = [{ wch: 36 }, { wch: 64 }];
      wsEntries["!cols"] = [
        { wch: 12 },
        { wch: 18 },
        { wch: 14 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 10 },
        { wch: 26 },
        { wch: 40 },
        { wch: 40 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
      XLSX.utils.book_append_sheet(wb, wsEntries, "Entries");

      XLSX.writeFile(wb, `${fileBase}.xlsx`);
      return showToast("success", "Excel exported ✅");
    }

    if (kind === "pdf") {
      const doc = new jsPDF("l", "pt", "a4");
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 36;

      doc.setFontSize(16);
      doc.text("Cashbook Report", marginX, 34);

      doc.setFontSize(10);
      const metaLines = [
        `Generated: ${meta.generatedAt}`,
        `Employee: ${meta.employeeLabel}`,
        `Location: ${meta.locationLabel}`,
        `Date Range: ${meta.rangeLabel}`,
        `Rows: ${meta.rowCount}`,
        `Note: Receipts are included in XLSX export.`,
      ];
      metaLines.forEach((t, i) => doc.text(t, marginX, 52 + i * 14));

      const summaryTable = [
        ["Opening (₹)", fmt(meta.opening)],
        ["Total Cash In / Received (₹)", fmt(meta.totalIn)],
        ["Total Cash Out / Spent (₹)", fmt(meta.totalOut)],
        ["Receivable (Company → Employee) (₹)", fmt(meta.receivable)],
        ["Advance Left (Employee holds) (₹)", fmt(meta.advanceLeft)],
        ["Final Balance (₹)", fmt(meta.finalBal)],
        ["Balance Now (₹)", meta.balNow == null ? "—" : fmt(meta.balNow)],
      ];

      autoTable(doc, {
        startY: 150,
        head: [["Summary", "Amount"]],
        body: summaryTable,
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 5, overflow: "linebreak" },
        headStyles: { fillColor: [11, 26, 51] },
        columnStyles: {
          0: { cellWidth: 320 },
          1: { cellWidth: 160, halign: "right" },
        },
        margin: { left: marginX, right: marginX },
      });

      const startY = (doc.lastAutoTable?.finalY || 200) + 18;

      const head = [[
        "Date",
        "Emp",
        "Type",
        "In (₹)",
        "Out (₹)",
        "Bal (₹)",
        "Mode",
        "Location",
        "Remark",
      ]];

      const body = visibleRows.map((r) => [
        r.date ? fmtDate(r.date) : "—",
        r.userName || r.user || "-",
        String(r.type || "").replaceAll("_", " "),
        r.type === "cash_in" || r.type === "opening" ? fmt(r.amountINR) : "",
        r.type === "cash_out" ? fmt(r.amountINR) : "",
        fmt(r.__runningBalance),
        r.mode || "",
        r.siteName || r.masterSiteName || r.workName || "",
        r.remark || "-",
      ]);

      const availableW = pageW - marginX - marginX;
      const baseWidths = [78, 78, 62, 74, 74, 78, 58, 190, 260];
      const baseSum = baseWidths.reduce((a, b) => a + b, 0);
      const scale = baseSum > availableW ? availableW / baseSum : 1;
      const widths = baseWidths.map((w) => Math.max(40, Math.floor(w * scale)));

      const colStyles = {};
      widths.forEach((w, i) => (colStyles[i] = { cellWidth: w }));
      colStyles[3].halign = "right";
      colStyles[4].halign = "right";
      colStyles[5].halign = "right";
      colStyles[8].overflow = "linebreak";

      autoTable(doc, {
        startY,
        head,
        body,
        theme: "striped",
        margin: { left: marginX, right: marginX, bottom: 34 },
        tableWidth: availableW,
        styles: {
          fontSize: 9,
          cellPadding: 4,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [11, 26, 51],
          textColor: 255,
          fontStyle: "bold",
          halign: "left",
        },
        columnStyles: colStyles,
        didDrawPage: (data) => {
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(9);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageW - marginX - 90,
            pageH - 16
          );
        },
      });

      doc.save(`${fileBase}.pdf`);
      return showToast("success", "PDF exported ✅");
    }
  };

  /* ---------------- options ---------------- */
  const userOptions = (people || []).map((p) => (
    <option key={p._id} value={p._id}>
      {p.username}
    </option>
  ));

  const masterSiteOptions = (masterSites || []).map((s) => (
    <option key={s._id} value={s._id}>
      {s.name || s._id}
    </option>
  ));

  const workOptions = (works || []).map((w, idx) => {
    const wid = toId(w?._id || w?.id || "");
    const wname = String(w?.name || w?.title || w?.label || w).trim();
    return (
      <option key={wid || `w-${idx}`} value={wid} disabled={!wid}>
        {wname}
      </option>
    );
  });

  const siteOptionsForAdd = (filteredSitesForAddDropdown || []).map((s) => (
    <option key={s._id} value={s._id}>
      {s.name || s._id}
    </option>
  ));

  const headerError =
    errors.list || errors.summary
      ? [
        errors.list ? `List: ${errors.list}` : null,
        errors.summary ? `Summary: ${errors.summary}` : null,
      ]
        .filter(Boolean)
        .join("  •  ")
      : "";

  return (
    <div className="tech-page finance-page cashbook-compact">
      {toast && (
        <div
          className={`toast toast-${toast.type}`}
          onClick={() => setToast(null)}
        >
          {toast.msg}
        </div>
      )}

      <section className="cashbook-topbar">
        <div className="cashbook-title">
          <div className="h">
            {isReviewer ? "Cashbook Review" : "My Cashbook"}
          </div>
          <div className="p">
            {isReviewer
              ? "Filter by employee / location / date"
              : "Your entries, balance & exports"}
          </div>
        </div>

        <div className="cashbook-top-actions">
          {isReviewer && (
            <button
              className={`btn ${onlyMyEntries ? "ghost active-toggle" : ""}`}
              type="button"
              onClick={() => {
                setOnlyMyEntries((prev) => !prev);
                setSelectedUserId("");
              }}
              title="Show only my entries"
            >
              {onlyMyEntries ? "Showing My Entries" : "Only My Entries"}
            </button>
          )}

          <button
            className="btn primary"
            type="button"
            onClick={() => setDrawerOpen(true)}
          >
            + Add Entry
          </button>
        </div>
      </section>

      <section className="card cashbook-panel">
        <div className="cashbook-grid">
          <div className="field">
            <label>Search</label>
            <input
              className="input"
              value={qSearch}
              onChange={(e) => setQSearch(e.target.value)}
              placeholder="user / remark / location / amount..."
            />
          </div>

          {isReviewer ? (
            <div className="field">
              <label>Employee</label>
              <select
                className="input"
                value={onlyMyEntries ? user?._id || "" : selectedUserId}
                onChange={(e) => setSelectedUserId(toId(e.target.value))}
                disabled={onlyMyEntries}
              >
                <option value="">
                  {onlyMyEntries ? "My entries only" : "All employees"}
                </option>
                {!onlyMyEntries && userOptions}
              </select>
            </div>
          ) : (
            <div className="field">
              <label>Employee</label>
              <input className="input" value={user?.username || "-"} disabled />
            </div>
          )}

          <div className="field">
            <label>Location Type</label>
            <select
              className="input"
              value={locationType}
              onChange={(e) => {
                const t = e.target.value;
                setLocationType(t);
                setWorkFilterId("");
                setMasterFilterId("");
                setSiteFilterId("");
              }}
            >
              <option value="">All</option>
              <option value="masterSite">MasterSite</option>
              <option value="work">Work</option>
            </select>
          </div>

          <div className="field">
            <label>Location</label>

            {!locationType && (
              <input className="input" disabled value="All locations" />
            )}

            {locationType === "work" && (
              <select
                className="input"
                value={workFilterId}
                onChange={(e) => setWorkFilterId(toId(e.target.value))}
              >
                <option value="">All works</option>
                {workOptions}
              </select>
            )}

            {locationType === "masterSite" && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <select
                  className="input"
                  value={masterFilterId}
                  onChange={(e) => {
                    const ms = toId(e.target.value);
                    setMasterFilterId(ms);
                    setSiteFilterId("");
                  }}
                  title="MasterSite"
                >
                  <option value="">All master sites</option>
                  {masterSiteOptions}
                </select>

                <select
                  className="input"
                  value={siteFilterId}
                  onChange={(e) => setSiteFilterId(toId(e.target.value))}
                  disabled={!masterFilterId}
                  title="Site under selected master"
                >
                  <option value="">
                    {masterFilterId
                      ? "All sites (under selected master)"
                      : "Select master site first"}
                  </option>
                  {reviewSitesUnderMaster.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name || s._id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="field">
            <label>From</label>
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="field">
            <label>To</label>
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        <div className="cashbook-actions-row">
          <div className="left">
            {headerError ? (
              <div className="inline-error">{headerError}</div>
            ) : null}
            {errors.balance ? (
              <div className="inline-hint">Balance: {errors.balance}</div>
            ) : null}
          </div>

          <div className="right">
            <button
              className="btn"
              type="button"
              onClick={() => {
                setFrom("");
                setTo("");
                clearLocation();
                if (isReviewer) setSelectedUserId("");
                setOnlyMyEntries(false);
                setQSearch("");
                showToast("info", "Cleared");
              }}
            >
              Clear
            </button>
            <button className="btn" type="button" onClick={applyNow}>
              Apply
            </button>
            <button
              className="btn ghost"
              type="button"
              onClick={() => handleExport("pdf")}
            >
              PDF
            </button>
            <button
              className="btn ghost"
              type="button"
              onClick={() => handleExport("xlsx")}
            >
              XLSX
            </button>
          </div>
        </div>
      </section>

      <section className="cashbook-stats">
        <div className="stat">
          <div className="k">Opening</div>
          <div className="v">₹ {fmt(summary?.openingBalance)}</div>
        </div>
        <div className="stat">
          <div className="k">Total In</div>
          <div className="v">₹ {fmt(summary?.totalCashIn)}</div>
        </div>
        <div className="stat">
          <div className="k">Total Out</div>
          <div className="v">₹ {fmt(summary?.totalCashOut)}</div>
        </div>
        <div className="stat">
          <div className="k">Final</div>
          <div className="v">₹ {fmt(summary?.finalBalance)}</div>
        </div>
        <div className="stat highlight">
          <div className="k">Balance (Now)</div>
          <div className="v">
            {balance == null ? "—" : `₹ ${fmt(balance)}`}
          </div>
        </div>
      </section>

      <section className="card table-card compact-table">
        <div
          className="table-header"
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
            <div className="title">Entries</div>
            <div className="count">
              {visibleRows.length} row{visibleRows.length === 1 ? "" : "s"}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              className="input"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) || 15)}
              title="Rows per page"
              style={{ width: 110 }}
            >
              <option value={10}>10 / page</option>
              <option value={15}>15 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>

            <button
              className="btn mini"
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              title="First"
            >
              «
            </button>
            <button
              className="btn mini"
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              title="Previous"
            >
              ‹
            </button>

            <div
              className="muted"
              style={{
                fontSize: 12,
                fontWeight: 900,
                minWidth: 110,
                textAlign: "center",
              }}
            >
              Page {page} / {totalPages}
            </div>

            <button
              className="btn mini"
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              title="Next"
            >
              ›
            </button>
            <button
              className="btn mini"
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              title="Last"
            >
              »
            </button>
          </div>
        </div>

        <div className="table-scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Type</th>
                <th className="right">In</th>
                <th className="right">Out</th>
                <th className="right">Bal</th>
                <th>Mode</th>
                <th>Location</th>
                <th>Receipt</th>
                <th>Remark</th>
                <th className="center">Action</th>
              </tr>
            </thead>

            <tbody>
              {pagedRows.map((r, i) => {
                const isOpening = r._id === "__opening__";
                const canEdit = !isOpening && canSelfModify(r);
                const canDelete = !isOpening && canDeleteWithinDays(r, 15);

                return (
                  <tr
                    key={r._id || `row-${(page - 1) * pageSize + i}`}
                    className={isOpening ? "row-opening" : ""}
                  >
                    <td className="nowrap">{fmtDate(r.date)}</td>
                    <td className="cell-strong">{r.userName || r.user || "-"}</td>
                    <td className="nowrap pill">
                      {String(r.type || "").replace("_", " ")}
                    </td>

                    <td className="right strong">
                      {r.type === "cash_in" || r.type === "opening"
                        ? fmt(r.amountINR)
                        : ""}
                    </td>
                    <td className="right strong">
                      {r.type === "cash_out" ? fmt(r.amountINR) : ""}
                    </td>
                    <td className="right strong">{fmt(r.__runningBalance)}</td>

                    <td className="nowrap">{r.mode || ""}</td>
                    <td className="nowrap">
                      {r.siteName || r.masterSiteName || r.workName || ""}
                    </td>

                    <td>
                      {Array.isArray(r.attachments) && r.attachments.length ? (
                        <a
                          className="receipt-link"
                          href={getAttachmentUrl(r.attachments[0])}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {getAttachmentLabel(r.attachments[0], 0)}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="remark-cell">
                      {r.remark || "-"}
                    </td>

                    <td className="center">
                      {isOpening ? (
                        "—"
                      ) : canEdit || canDelete ? (
                        <div className="row-actions">
                          {canEdit && (
                            <button
                              className="btn-link"
                              type="button"
                              onClick={() => openEdit(r)}
                            >
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn-link danger"
                              type="button"
                              onClick={() => handleDelete(r)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="row-actions-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!pagedRows.length && (
                <tr>
                  <td
                    colSpan="11"
                    className="center muted"
                    style={{ padding: 16, fontWeight: 900 }}
                  >
                    No entries
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="card modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="title">Add Entry (Self)</div>
              <button
                className="btn mini"
                type="button"
                onClick={() => {
                  resetAddForm();
                  setDrawerOpen(false);
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAdd} className="modal-form">
              <div className="grid-2">
                <div className="field">
                  <label>Date</label>
                  <input
                    className="input"
                    type="date"
                    value={form.date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, date: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="field">
                  <label>Type</label>
                  <select
                    className="input"
                    value={form.type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, type: e.target.value }))
                    }
                  >
                    <option value="cash_out">Cash Out (Spent)</option>
                    <option value="cash_in">Cash In (Received)</option>
                    <option value="opening">Opening</option>
                  </select>
                </div>
              </div>

              <div className="grid-2">
                <div className="field">
                  <label>Amount</label>
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={form.amountINR}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, amountINR: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="field">
                  <label>Mode</label>
                  <select
                    className="input"
                    value={form.mode}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, mode: e.target.value }))
                    }
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Bank">Bank</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
              </div>

              <div className="grid-2">
                <div className="field">
                  <label>MasterSite</label>
                  <select
                    className="input"
                    value={form.masterSite}
                    onChange={(e) => {
                      const val = toId(e.target.value);
                      setSiteMasterFilterId(val);
                      setForm((f) => ({
                        ...f,
                        masterSite: val,
                        site: val ? "" : toId(f.site),
                        work: val ? "" : toId(f.work),
                      }));
                    }}
                  >
                    <option value="">(none)</option>
                    {masterSiteOptions}
                  </select>
                </div>

                <div className="field">
                  <label>Site</label>
                  <select
                    className="input"
                    value={form.site}
                    onChange={(e) => {
                      const val = toId(e.target.value);
                      setForm((f) => ({
                        ...f,
                        site: val,
                        masterSite: "",
                        work: "",
                      }));
                    }}
                    disabled={!siteMasterFilterId && !form.site}
                  >
                    <option value="">
                      {!siteMasterFilterId && !form.site
                        ? "(select master site first)"
                        : "(none)"}
                    </option>
                    {siteOptionsForAdd}
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Work</label>
                <select
                  className="input"
                  value={form.work}
                  onChange={(e) => {
                    const val = toId(e.target.value);
                    setSiteMasterFilterId("");
                    setForm((f) => ({
                      ...f,
                      work: val,
                      masterSite: "",
                      site: "",
                    }));
                  }}
                >
                  <option value="">(none)</option>
                  {workOptions}
                </select>
              </div>

              <div className="field">
                <label>Remark</label>
                <input
                  className="input"
                  type="text"
                  value={form.remark}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, remark: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="drawer-receipts">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                  accept="image/*,application/pdf"
                />
                <button
                  type="button"
                  className="btn mini"
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                >
                  Upload
                </button>

                {form.attachments?.length ? (
                  <div className="chips">
                    {form.attachments.map((att, idx) => (
                      <a
                        key={`add-att-${att?.url || att?.originalName || idx
                          }-${idx}`}
                        href={getAttachmentUrl(att)}
                        target="_blank"
                        rel="noreferrer"
                        className="chip"
                      >
                        {getAttachmentLabel(att, idx)}
                      </a>
                    ))}
                  </div>
                ) : (
                  <span
                    className="muted"
                    style={{ fontWeight: 900, fontSize: 12 }}
                  >
                    No receipts
                  </span>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn mini"
                  onClick={() => setDrawerOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary mini"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="drawer-overlay" onClick={closeEdit}>
          <div className="card modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="title">Edit Entry</div>
              <button className="btn mini" type="button" onClick={closeEdit}>
                ✕
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div className="grid-2">
                <input
                  className="input"
                  type="date"
                  value={editForm.date}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, date: e.target.value }))
                  }
                />
                <select
                  className="input"
                  value={editForm.type}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, type: e.target.value }))
                  }
                >
                  <option value="cash_out">Cash Out</option>
                  <option value="cash_in">Cash In</option>
                  <option value="opening">Opening</option>
                </select>
              </div>

              <div className="grid-2">
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  placeholder="₹"
                  value={editForm.amountINR}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, amountINR: e.target.value }))
                  }
                />
                <select
                  className="input"
                  value={editForm.mode}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, mode: e.target.value }))
                  }
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank">Bank</option>
                  <option value="Card">Card</option>
                </select>
              </div>

              <input
                className="input"
                placeholder="Remark"
                value={editForm.remark}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, remark: e.target.value }))
                }
              />

              <div className="modal-actions">
                <button className="btn mini" type="button" onClick={closeEdit}>
                  Cancel
                </button>
                <button
                  className="btn primary mini"
                  type="button"
                  onClick={submitEdit}
                >
                  Save
                </button>
              </div>

              <div className="muted" style={{ fontSize: 11, fontWeight: 900 }}>
                Edit allowed only within 24 hours. Delete allowed for your own
                entries within 15 days.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}